import * as _ from 'lodash';
import * as moment from 'moment';
import axios from 'axios';

import {
    IResponse,
    IProject,
    IProjectsParams,
    IProjectFilters,
    IProjectsSearchOptions,
    IProjectFilterItem,
    IProjectAttributesData,
    IProjectAttribute,
    IChildrenProjectsParams,
} from 'src/models';
import { PAGE_SIZE, PROJECTS_OPTIONS_PARAMS_MAPPING, PROJECT_OVERDUE_TIME, PROJECT_SN_PREFIX } from 'src/consts';
import { EProjectAttributeType, EProjectItemType, ESearchMode } from 'src/enums';
import { TProjectAttributesScene } from 'src/types';

import timeService from './time.service';
import toasterService from './toaster.service';
import uiService from './ui.service';

interface IProjectGroup {
    [key: string]: IProject[];
}

// 项目是否被高亮标记
function getIsProjectIsMarkedSync(project: IProject, matched_project_ids: string[]): boolean {
    return -1 !== _.indexOf(matched_project_ids, project.id);
}

// 分配ProjectGroup _is_marked
function assignProjectGroupIsMarkedSync(
    projectGroup: IProjectGroup,
    matched_project_ids: string[]
): void {
    _.forEach(projectGroup, (projects) => {
        _.forEach(projects, (project) => {
            project._is_marked = getIsProjectIsMarkedSync(project, matched_project_ids);
        });
    });
}

// 分配ProjectGroup _is_offspring_marked
function assignProjectGroupIsOffspringMarkedSync(
    projectGroup: IProjectGroup,
    matched_project_ids: string[]
): void {
    _.forEach(projectGroup, (projects) => {
        _.forEach(projects, (project) => {
            project._is_offspring_marked = getIsOffspringMarkedSync(
                project,
                projectGroup,
                matched_project_ids
            );
        });
    });
}

// 一级项目列表分配高亮标记
function assignLevelOneProjectsIsMarkedSync(projects: IProject[]): void {
    _.forEach(projects, project => {
        project._is_marked = getIsProjectIsMarkedSync(project, project.matched_project_ids);
    });
}

// 给一级项目分配_is_offspring_marked
function assignLevelOneProjectsIsOffspringMarkedSync(
    projects: IProject[]
): void {
    _.forEach(projects, (project) => {
        if (project.matched_project_ids?.length) {
            project._is_offspring_marked = _.without(project.matched_project_ids, project.id).length > 0;
        } else {
            project._is_offspring_marked = false;
        }
    });
}

// 后代节点中存在被高亮的
function getIsOffspringMarkedSync(
    project: IProject,
    projectGroup: IProjectGroup,
    matched_project_ids: string[]
): boolean {
    let isOffspringTitleMarked = false;
    const children = projectGroup[project.id];
    if (children?.length) {
        isOffspringTitleMarked =
            isOffspringTitleMarked ||
            // 子项目被高亮
            _.some(children, (child) => {
                return getIsProjectIsMarkedSync(child, matched_project_ids);
            });
        isOffspringTitleMarked =
            isOffspringTitleMarked ||
            // 子项目的后代项目被高亮
            _.some(children, (child) => {
                return getIsOffspringMarkedSync(child, projectGroup, matched_project_ids);
            });
    }
    return isOffspringTitleMarked;
}

// 暂存获取项目列表数据，若重复请求，则返回暂存数据，以减少请求压力
let tempProjectsCountParams: IProjectsParams;
let tempProjectsCount = 0;

export default {
    // 缓存的子项目组 IProjectGroup（以parent_id为key）
    /*private*/ cacheProjectGroup: {},

    // 缓存项目children, 并标记_is_offspring_marked
    /*private*/ saveCacheProjectGroup(
    projectId: string,
    matched_project_ids: string[], // 关键字需高亮的projectIds
    searchMode: ESearchMode
): Promise<IProjectGroup> {
        let group: IProjectGroup;
        // 搜索模式为精准模式时，增加sub_ids参数
        const searchParams = searchMode === ESearchMode.Precious && matched_project_ids  ? { sub_ids: matched_project_ids } : {};

        return (searchParams.sub_ids?.length > 50 // 超过 50 条使用 post 请求
            ? axios.post<IProject[]>(`/api/projects/${projectId}/tree-full`, searchParams)
            : axios.get<IProject[]>(`/api/projects/${projectId}/tree-full`, { params: searchParams })
        ).then((res) => {
            const projects = _.reject(res.data, { id: projectId }); // 移除本项目
            // 因api中过程调度的 parent_id 不对，故先通过 parent_puid 来修正 parent_id
            _.forEach(projects, (project) => {
                const parent = _.find(projects, { puid: project.parent_puid });
                project.parent_id = parent ? parent.id : projectId;
                // matched_project_ids取父项目中的值
                project.matched_project_ids = matched_project_ids;
            });
            group = _.groupBy(projects, 'parent_id');
            assignProjectGroupIsMarkedSync(group, matched_project_ids);
            assignProjectGroupIsOffspringMarkedSync(group, matched_project_ids);
            this.cacheProjectGroup = Object.assign(this.cacheProjectGroup, group);  // 合并缓存
            return this.cacheProjectGroup;
        });
    },

    // 获取缓存的项目列表
    /*private*/ getCacheProjectChildren(
        projectId: string,
        params: IChildrenProjectsParams
    ): Promise<IProject[]> {
        let children = _.cloneDeep(this.cacheProjectGroup[projectId]);
        // children = this.unzipProjectsDelayEndOn(children);
        children = uiService.unzipAuditProjectsIsNeedReceiptSync(children);
        children = uiService.unzipAuditProjectsIsSecretSync(children);
        children = uiService.unzipAuditProjectsEndOnSync(children);
        uiService.assignProjectReadMarksSync(children);
        return uiService.getCutKeywords(params.q).then((keywords) => {
            uiService.assignProjectMarkedPropertiesSync(children, keywords);
            return children;
        });
    },

    // 获取项目列表
    getProjects(params?: IProjectsParams): Promise<IProject[]> {
        if (!params.page) {
            params.page = 1;
        }
        if (!params.limit) {
            params.limit = PAGE_SIZE;
        }
        let projects = [];
        return axios
            .get<IProject[]>('/api/projects', { params })
            .then((res) => {
                projects = res.data;
                // 没有q时，不需高亮，将matched_project_ids置空
                if (!params.q) {
                    _.forEach(projects, project => {
                        project.matched_project_ids = null;
                    });
                }
                // projects = this.unzipProjectsDelayEndOn(projects);
                projects = uiService.unzipAuditProjectsIsNeedReceiptSync(projects);
                projects = uiService.unzipAuditProjectsIsSecretSync(projects);
                projects = uiService.unzipAuditProjectsEndOnSync(projects);
                uiService.assignProjectsSnSync(projects, params.page, params.limit);
                uiService.assignProjectReadMarksSync(projects);
                return uiService.getCutKeywords(params.q, true);
            })
            .then((keywords) => {
                uiService.assignProjectMarkedPropertiesSync(projects, keywords);
                assignLevelOneProjectsIsMarkedSync(projects);
                assignLevelOneProjectsIsOffspringMarkedSync(projects);
                return projects;
            });
    },

    // 获取项目列表count
    getProjectsCount(params?: IProjectsParams): Promise<number> {
        // 请求参数一样（page、limit除外），则返回暂存的数据
        if (
            _.isEqual(
                _.omit(params, ['page', 'limit']),
                _.omit(tempProjectsCountParams, ['page', 'limit'])
            )
        ) {
            return Promise.resolve(tempProjectsCount);
        } else {
            tempProjectsCountParams = _.cloneDeep(params);
            return axios.get<number>('/api/projects-count', { params }).then((res) => {
                tempProjectsCount = res.data;
                return tempProjectsCount;
            });
        }
    },

    // 获取子项目列表
    getChildrenProjects(projectId: string, params: IChildrenProjectsParams): Promise<IProject[]> {
        let projects = [];
        return axios
            .get<IProject[]>(`/api/projects/${projectId}/children`, { params })
            .then((res) => {
                projects = res.data;
                // projects = this.unzipProjectsDelayEndOn(projects);
                projects = uiService.unzipAuditProjectsIsNeedReceiptSync(projects);
                projects = uiService.unzipAuditProjectsIsSecretSync(projects);
                projects = uiService.unzipAuditProjectsEndOnSync(projects);
                uiService.assignProjectReadMarksSync(projects);
                return uiService.getCutKeywords(params.q);
            })
            .then((keywords) => {
                uiService.assignProjectMarkedPropertiesSync(projects, keywords);
                return projects;
            });
    },

    //#region 项目中心
    // 获取项目中心项目列表
    getProjectCenterProjects(params: IProjectsParams): Promise<IProject[]> {
        params = _.cloneDeep(params);
        params.include_schedule = true; // 获取过程调度
        return this.getProjects(params);
    },

    // 获取项目中心项目列表count
    getProjectCenterProjectsCount(params: IProjectsParams): Promise<number> {
        params = _.cloneDeep(params);
        params.include_schedule = true; // 获取过程调度
        return this.getProjectsCount(params);
    },

    // 获取项目中心子项目列表
    getProjectCenterChildrenProjects(
        projectId: string,
        matched_project_ids: string[],
        params: IChildrenProjectsParams,
        searchMode: ESearchMode,
        isLevelOne?: boolean /*当前项目是一级项目*/
    ): Promise<IProject[]> {
        params = _.cloneDeep(params);
        params.include_schedule = true; // 获取过程调度
        return (isLevelOne ? this.saveCacheProjectGroup(projectId, matched_project_ids, searchMode) : Promise.resolve())
            .then(() => {
                return this.getCacheProjectChildren(projectId, params);
            });
    },

    // 通过邮件导出项目中心报表
    getProjectCenterProjectsReportByEmail(params: IProjectsParams): Promise<any> {
        params = _.cloneDeep(params);
        params.include_schedule = true; // 获取过程调度
        return axios.get<any>('/api/report/projects/email', { params }).then((res) => res.data);
    },
    //#endregion

    //#region 立项发布-已下发
    // 获取已下发项目列表
    getSentProjects(params: IProjectsParams): Promise<IProject[]> {
        params = _.cloneDeep(params);
        params.include_schedule = true; // 获取过程调度
        params.order = '0'; // 排序规则：创建时间
        params.role = '50'; // 项目角色：我下发的
        params.status = ['un-received', 'un-submitted', 'in-flowing', 'completed', 'canceled']; // 流程状态：已接收||未提交||流程中||已完成||已取消
        params.type = ['Year', 'Month', 'Specialized']; // 项目类型：年度重点项目||月度重点项目||专项
        return this.getProjects(params);
    },

    // 获取已下发项目列表count
    getSentProjectsCount(params: IProjectsParams): Promise<number> {
        params = _.cloneDeep(params);
        params.include_schedule = true; // 获取过程调度
        params.order = '0'; // 排序规则：创建时间
        params.role = '50'; // 项目角色：我下发的
        params.status = ['un-received', 'un-submitted', 'in-flowing', 'completed', 'canceled']; // 流程状态：已接收||未提交||流程中||已完成||已取消
        params.type = ['Year', 'Month', 'Specialized']; // 项目类型：年度重点项目||月度重点项目||专项
        return this.getProjectsCount(params);
    },

    // 获取已下发子项目列表
    getSentChildrenProjects(
        projectId: string,
        params: IChildrenProjectsParams
    ): Promise<IProject[]> {
        params = _.cloneDeep(params);
        params.include_schedule = true; // 获取过程调度
        params.order = '0'; // 排序规则：创建时间
        params.role = '50'; // 项目角色：我下发的
        params.status = ['un-received', 'un-submitted', 'in-flowing', 'completed', 'canceled']; // 流程状态：已接收||未提交||流程中||已完成||已取消
        params.type = ['Year', 'Month', 'Specialized']; // 项目类型：年度重点项目||月度重点项目||专项
        return this.getChildrenProjects(projectId, params);
    },
    //#endregion

    // 获取项目详情
    getProject(id: string): Promise<IProject> {
        return axios.get<IProject>(`/api/projects/${id}`).then((res) => {
            // let projects = this.unzipProjectsDelayEndOn([res.data]);
            let projects = [res.data];
            projects = uiService.unzipAuditProjectsIsNeedReceiptSync(projects);
            projects = uiService.unzipAuditProjectsIsSecretSync(projects);
            projects = uiService.unzipAuditProjectsEndOnSync(projects);
            return projects[0];
        });
    },

    // 通过puid获取项目详情
    getProjectByPuid(puid: string): Promise<IProject> {
        return axios.get<IProject>(`/api/projects/puids/${puid}`).then((res) => {
            // let projects = this.unzipProjectsDelayEndOn([res.data]);
            let projects = [res.data];
            projects = uiService.unzipAuditProjectsIsNeedReceiptSync(projects);
            projects = uiService.unzipAuditProjectsIsSecretSync(projects);
            projects = uiService.unzipAuditProjectsEndOnSync(projects);
            return projects[0];
        });
    },

    // 关注
    followProject(projectId: string): Promise<IResponse> {
        return axios.post<IResponse>(`/api/projects/${projectId}/follows`).then((res) => res.data);
    },

    // 取消关注
    unfollowProject(projectId: string): Promise<IResponse> {
        return axios
            .delete<IResponse>(`/api/projects/${projectId}/follows`)
            .then((res) => res.data);
    },

    // 删除项目
    deleteProject(id: string, item_type: EProjectItemType): Promise<IResponse> {
        return axios
            .delete<IResponse>(`/api/projects/${id}?item_type=${item_type}`)
            .then((res) => res.data);
    },

    // 获取项目筛选条件
    getProjectFilters(): Promise<IProjectFilters> {
        return axios.get<IProjectsSearchOptions>('/api/projects/search-options').then((res) => {
            const searchOptions: IProjectsSearchOptions = res.data;
            const mapping = PROJECTS_OPTIONS_PARAMS_MAPPING;
            const filters: IProjectFilters = {};
            _.forEach(searchOptions, (optionItems, key) => {
                const items: IProjectFilterItem[] = [];
                _.forEach(optionItems, (item) => {
                    items.push({
                        label: item.value,
                        value: item.key,
                        description: item.description
                    });
                });
                filters[mapping[key].paramKey] = {
                    title: mapping[key].title,
                    items,
                };
            });
            return filters;
        });
    },

    // 获取项目（下发|变更）的项目属性列表（项目群、项目类型、项目级别、业务分类）
    getProjectAttributesData(scene: TProjectAttributesScene): Promise<IProjectAttributesData> {
        return axios
            .get<IProjectAttributesData>('/api/projects/project-attributes', {
                params: { scene },
            })
            .then((res) => {
                const projectAttributes = _.sortBy(res.data as unknown as IProjectAttribute[], 'rank');
                return {
                    groups: _.filter(projectAttributes, { type: EProjectAttributeType.Group }),
                    levels: _.filter(projectAttributes, { type: EProjectAttributeType.Level }),
                    types: _.filter(projectAttributes, { type: EProjectAttributeType.Type }),
                    categories: _.filter(projectAttributes, {
                        type: EProjectAttributeType.Category,
                    }),
                };
            });
    },

    // 记录已读标记
    saveProjectReadMarkSync(project: IProject) {
        project._is_read = true;
        const today = new Date();
        if (!project.down_time) {
            return;
        }
        if (
            today.getTime() - timeService.utc2gmt(project.down_time).getTime() >
            PROJECT_OVERDUE_TIME
        ) {
            return;
        }
        const projectReadMarks = JSON.parse(localStorage.getItem('projectReadMarks')) || {};
        projectReadMarks[project.id] = project.down_time;
        localStorage.setItem('projectReadMarks', JSON.stringify(projectReadMarks));
    },

    // 判断项目是否是新建的项目（项目下发单的项目、项目变更单追加的项目）
    // (项目没有id， 或为’0‘)
    getProjectIsNewSync(project: IProject): boolean {
        return !project.id || '0' === project.id;
    },

    //#region 验证项目数据合法性
    // 验证项目列表的数据合法性
    verifyProjectsSync(params: { scene: TProjectAttributesScene; projects: IProject[] }): boolean {
        const scene = params.scene;
        const projects = params.projects;
        let valid = this.verifyProjectsNotEmpty(scene, projects);
        if (!valid) {
            return false;
        }

        _.forEach(projects, (project) => {
            valid = this.verifyProjectSync(project);
            if (!valid) {
                return false;
            }
        });
        return valid;
    },

    // 验证项目列表不为空
    verifyProjectsNotEmpty(scene: TProjectAttributesScene, projects: IProject[]): boolean {
        let content = '';
        switch (scene) {
            case 'project-issue':
                content = '请单建项目';
                break;
            case 'project-change':
                content = '请变更项目';
                break;
        }
        const valid = !!projects.length;
        if (!valid) {
            toasterService.show({ type: 'danger', title: content });
        }
        return valid;
    },

    // 验证单个项目的数据合法性
    verifyProjectSync(
        project: IProject,
        projectAttributesData?: IProjectAttributesData
    ): boolean {
        return this.verifyEndOnFormSync(project)
            // && this.verifyEndOnIsNotBeforeTodaySync(project) // 移除校验，以处理：超期的项目在变更时，在未修改完成时间的情况下，也校验了完成时间不得早于当前时间
            && this.verifyProjectAttributesValid(projectAttributesData, project);
    },

    // 验证完成时间格式
    verifyEndOnFormSync(project: IProject): boolean {
        const valid = moment(project._end_on).isValid();
        if (!valid) {
            toasterService.show({
                type: 'danger',
                title: '项目完成时间格式不正确',
            });
        }
        return valid;
    },

    // 完成时间不得早于当前时间
    verifyEndOnIsNotBeforeTodaySync(project: IProject): boolean {
        // const endOn = moment(project._delay_end_on);
        const endOn = moment(project._end_on);
        const today = moment().format('YYYY-MM-DD');
        const valid = !endOn.isBefore(today);
        if (!valid) {
            toasterService.show({
                type: 'danger',
                title: '项目完成时间不可早于当前时间',
            });
        }
        return valid;
    },

    // 子项目的完成时间不可晚于父项目的完成时间
    verifyEndOnIsNotAfterParentSync(project: IProject, parentProject?: IProject): boolean {
        if (!parentProject) {
            return true;
        }
        // const valid = !moment(project._delay_end_on).isAfter(parentProject._delay_end_on);
        const valid = !moment(project._end_on).isAfter(parentProject._end_on);
        if (!valid) {
            toasterService.show({
                type: 'danger',
                title: '子项目完成时间不可晚于父项目完成时间',
            });
        }
        return valid;
    },
    //#endregion

    //#region 通过计算_delay_end_on，显示delay_on || end_on
    // _delay_end_on，根据is_delay决定取delay_on还是end_on
    // unzipProjectsDelayEndOn(projects: IProject[]): IProject[] {
    //     projects = _.cloneDeep(projects);
    //     _.forEach(projects, project => {
    //         project._delay_end_on = project.is_delayed ? project.delay_on : project.end_on;
    //     });
    //     return projects;
    // },

    // _delay_end_on赋值给end_on
    // zipProjectsDelayEndOn(projects: IProject[]): IProject[] {
    //     projects = _.cloneDeep(projects);
    //     _.forEach(projects, project => {
    //         project.end_on = project._delay_end_on
    //     });
    //     return projects;
    // }
    //#endregion

    // 项目的 _sn 前加上前缀，用于 http 拦截器判断是项目数据错误
    zipAuditProjectsSnSync(projects: IProject[]): IProject[] {
        projects = _.cloneDeep(projects);
        _.forEach(projects, project => {
            project._sn = PROJECT_SN_PREFIX + project._sn;
        });
        return projects;
    },

    // 校验项目字段合法性（是否被删除、禁用或编码已修改）
    verifyProjectAttributesValid(projectAttributesData: IProjectAttributesData, project: IProject): boolean {
        if (!this.getProjectIsNewSync(project) || !projectAttributesData) { // 不校验已下发的项目
            return true;
        }
        return this.verifyProjectAttributeValid(projectAttributesData.types, project.type_code, '项目类型')
            && this.verifyProjectAttributeValid(projectAttributesData.levels, project.level_code, '项目级别')
            && this.verifyProjectAttributeValid(projectAttributesData.categories, project.category_code, '业务分类')
            && this.verifyProjectAttributeValid(projectAttributesData.groups, project.group_codes[0], '项目群');
    },

    // 校验项目类型、项目群、项目级别、业务分类
    verifyProjectAttributeValid(projectAttributes: IProjectAttribute[], projectAttributeCode: string, attributeName: string): boolean {
        if (!_.find(projectAttributes, { code: projectAttributeCode })) {
            toasterService.show({
                type: 'danger',
                title: `${attributeName}配置属性已变更，请重新设置`,
            });
            return false;
        }
        return true;
    }
};
